import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { useUnloadWarning } from "@transfr-inc/dashboard-components/hooks";
import { formatTimestap } from "@transfr-inc/dashboard-components/utils";

import { Product } from "@components/products/product";
import { useApiRequest } from "@lib/hooks";
import { createClassroomDetailLink } from "@lib/utils/navigation.utils";
import container from "@src/container";
import { RemoveTraineeModal } from "@views/users/trainee-detail/modals/remove-trainee";

import { TraineeSummaryStats } from "./trainee.ce.databoxes";

import "./trainee.insights.scss";

export function TraineeCeInsights({
  trainee,
  classroom,
  onRemoveTrainee,
  removeTraineeEnabled,
}) {
  const [masteryStats, setMasteryStats] = useState();
  const [openRemoveModal, setOpenRemoveModal] = useState();
  const { insightsService } = container;
  const { organizationCode, userId, firstName, lastName } = trainee;
  const history = useHistory();

  const { loading: downloadingFile, sendRequest: exportTraineeData } =
    useApiRequest(() => {
      const { classroomId } = classroom;

      const fileName = `${firstName}_${lastName}-${formatTimestap()}`;

      return insightsService.exportExplorerData(
        organizationCode,
        classroomId,
        userId,
        fileName
      );
    }, false);

  useUnloadWarning({ trigger: downloadingFile });

  useEffect(() => {
    if (classroom) {
      getUserMasteryStats();
    }
    return () => insightsService.cancelRequest();
  }, []);

  const getUserMasteryStats = () => {
    insightsService
      .getExplorerSummaryStats(trainee.userId)
      .then(setMasteryStats)
      .catch(() => setMasteryStats({}));
  };

  const goClassroomDetailsPage = () => {
    const location = createClassroomDetailLink({
      classroomId: classroom.classroomId,
      search: {
        product: Product.CE,
        from: `Trainee`,
      },
    });
    history.push(location);
  };

  const onRemoveTraineeHandler = (trainee, classroom) => {
    return onRemoveTrainee(trainee, classroom).then(() => setOpenRemoveModal());
  };

  return (
    <>
      <div className="insights-section">
        <div className="insights-header">
          <div className="title-section">
            <h2 className="title">Insights</h2>
          </div>
          <div className="actions-section">
            <Button
              size="small"
              icon={["fa-light", "file-export"]}
              disabled={downloadingFile}
              onClick={exportTraineeData}
            >
              Export Data (.csv)
            </Button>
            {removeTraineeEnabled && (
              <Button
                size="small"
                icon={["fa-light", "user-xmark"]}
                onClick={() => setOpenRemoveModal(true)}
              >
                Remove Trainee
              </Button>
            )}
            <Button
              size="small"
              primary
              icon={["fa-light", "screen-users"]}
              onClick={goClassroomDetailsPage}
            >
              View Classroom
            </Button>
          </div>
        </div>
        <TraineeSummaryStats
          className="ce"
          masteryStats={masteryStats}
        ></TraineeSummaryStats>
        <div className="notification-loader-container">
          {downloadingFile && (
            <Notification
              type={NotificationType.info}
              closable
              icon={["fa-solid", "spinner"]}
              iconConfig={{ spin: true }}
            >
              We’re preparing a download of{" "}
              <strong>{`${firstName} ${lastName}'s`}</strong> insights. Please
              wait...
            </Notification>
          )}
        </div>
      </div>
      {removeTraineeEnabled && (
        <RemoveTraineeModal
          open={openRemoveModal}
          onClose={() => setOpenRemoveModal(false)}
          onRemoveTrainee={onRemoveTraineeHandler}
          trainee={trainee}
          classroom={classroom}
        ></RemoveTraineeModal>
      )}
    </>
  );
}
